// ChatMarkdown — render AI assistant message content as sanitized
// Markdown (GFM tables + lists + code blocks).
//
// Why: AI responses often contain markdown like `| col | col |\n|---|---|`
// or `**bold**` / `### heading` / numbered lists; the old chat bubble
// rendered raw text, so operators saw vertical bars and stars instead
// of formatted output. User complaint screenshot 2026-05-16.
//
// Security: AI is an LLM and may emit dangerous content (XSS via
// `<script>`, `<img onerror=>`, `javascript:` URLs, etc.) either by
// hallucination or prompt injection. We mitigate with:
//   1. rehype-sanitize using the default `defaultSchema` — permits only
//      a strict allowlist of tags + attrs. No `<script>`, no `on*`
//      handlers, no `style`, no `javascript:` URLs.
//   2. remark-gfm enables GFM features (tables, task lists,
//      strikethrough, autolinks) WITHOUT enabling raw HTML pass-through.
//   3. Links open in new tab + rel=noopener+noreferrer.
//
// Bundle: react-markdown + remark-gfm + rehype-sanitize ≈ 40 KB gzipped.
// Loaded only when ChatBubble is open; no impact on dashboard initial
// page load (chat bubble is already its own React tree).

import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { useNavigate } from "react-router-dom";
import {
  MapPin, Target, Route as RouteIcon, ListChecks, User,
  Building, Layers, Briefcase, Key, Calendar, AlertTriangle, ChevronRight,
  ImageOff, X as XIcon,
} from "lucide-react";
import { apiFetch } from "../../lib/api-client.js";

interface ChatMarkdownProps {
  /** Raw markdown string from the AI assistant. May be empty during
   *  streaming — caller decides whether to show a spinner instead. */
  content: string;
}

// ── Entity chips (Plan B) ─────────────────────────────────────────────
//
// AI can wrap entity mentions in prose like:
//   Found 3 patrols by [李小明](#entity:officer/01HXXX) at
//   [DAO 案場](#entity:site/01HYYY) today.
//
// The `#entity:` prefix uses fragment-URL syntax so rehype-sanitize's
// default schema (which restricts schemes to http/https/mailto/tel)
// passes it through unchanged — fragments are treated as relative URLs.
// The link component detects the prefix + renders an inline chip with
// an icon + clickable navigation via react-router (no full page
// reload). Falls back to a regular external link when the href doesn't
// match the entity-chip pattern.

const ENTITY_CHIP_PREFIX = "#entity:";

type IconComp = React.ComponentType<{ className?: string }>;

interface EntityChipMeta {
  kind: string;
  id: string;
  route: string;
  Icon: IconComp;
}

function parseEntityChip(href: string): EntityChipMeta | null {
  if (!href || !href.startsWith(ENTITY_CHIP_PREFIX)) return null;
  const rest = href.slice(ENTITY_CHIP_PREFIX.length);
  const slash = rest.indexOf("/");
  if (slash <= 0 || slash >= rest.length - 1) return null;
  const kind = rest.slice(0, slash);
  const id = rest.slice(slash + 1);
  // ID hygiene — reject obviously malformed payloads early to avoid
  // navigating to garbage URLs when the AI hallucinates the syntax.
  if (!/^[A-Za-z0-9_-]+$/.test(id) || id.length > 64) return null;
  const e = encodeURIComponent(id);
  switch (kind) {
    case "site":       return { kind, id, route: `/dashboard/sites/${e}`,                 Icon: MapPin };
    case "location": return { kind, id, route: `/dashboard/locations?focus=${e}`,     Icon: Target };
    case "route":      return { kind, id, route: `/dashboard/routes?focus=${e}`,          Icon: RouteIcon };
    case "task":       return { kind, id, route: `/dashboard/tasks?focus=${e}`,           Icon: ListChecks };
    case "user":
    case "officer":    return { kind, id, route: `/dashboard/users?focus=${e}`,           Icon: User };
    case "building":   return { kind, id, route: `/dashboard/topology?focus=${e}`,        Icon: Building };
    case "floor":      return { kind, id, route: `/dashboard/topology?focus=${e}`,        Icon: Layers };
    case "client":     return { kind, id, route: `/dashboard/clients?focus=${e}`,         Icon: Briefcase };
    case "role":       return { kind, id, route: `/dashboard/roles?focus=${e}`,           Icon: Key };
    case "shift":      return { kind, id, route: `/dashboard/schedule?focus=${e}`,        Icon: Calendar };
    case "incident":   return { kind, id, route: `/dashboard/audit?incident=${e}`,        Icon: AlertTriangle };
    default: return null;
  }
}

// ── R2 image rendering (Plan A — display existing patrol photos) ──────
//
// AI can embed evidence images in prose via markdown:
//   ![photo of incident](r2:submissions/SID/photos/key.jpg)
//
// rehype-sanitize's defaultSchema only allows http/https in `src`; we
// extend protocols.src to include `r2` so the parsed `img` survives
// sanitization with its custom-scheme src. The img component override
// detects the `r2:` prefix and routes to a stateful loader that
// authenticates via apiFetch (cookie / Bearer flows through), wraps the
// response body in a blob URL, and renders an inline thumbnail with
// click-to-lightbox.

const SANITIZE_SCHEMA = {
  ...defaultSchema,
  protocols: {
    ...(defaultSchema.protocols ?? {}),
    src: [
      ...((defaultSchema.protocols?.src as string[] | undefined) ?? ["http", "https"]),
      "r2",
    ],
  },
} as const;

const R2_PREFIX = "r2:";

function R2Image({ r2Key, alt }: { r2Key: string; alt: string }): React.ReactElement {
  const [src, setSrc] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState(false);
  // r2Key format expected: submissions/<sid>/<...>. evidence endpoint
  // gates on the same prefix. Reject anything else early so we don't
  // try to fetch a hallucinated path.
  const safeKey = /^submissions\/[A-Za-z0-9_/.-]+$/.test(r2Key) && r2Key.length < 256 ? r2Key : null;
  useEffect(() => {
    if (!safeKey) { setErr("bad_key"); return; }
    let revoke: string | null = null;
    let cancelled = false;
    void apiFetch(`/v1/patrols/evidence/${safeKey}`, { credentials: "include" })
      .then((r) => r.ok ? r.blob() : Promise.reject(new Error(`http_${r.status}`)))
      .then((blob) => {
        if (cancelled) return;
        revoke = URL.createObjectURL(blob);
        setSrc(revoke);
      })
      .catch((e: Error) => { if (!cancelled) setErr(e.message); });
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [safeKey]);
  if (err) {
    return (
      <span className="inline-flex items-center gap-1 my-1 px-1.5 py-0.5 rounded border border-border bg-bg-raised text-2xs text-text-muted" title={err}>
        <ImageOff className="h-3 w-3" />
        圖片載入失敗
      </span>
    );
  }
  if (!src) {
    return (
      <span className="inline-block my-1 w-32 h-32 rounded border border-border bg-bg-raised animate-pulse" aria-label="載入中" />
    );
  }
  return (
    <>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onClick={() => setLightbox(true)}
        className="my-1 max-h-48 max-w-full rounded border border-border cursor-zoom-in hover:opacity-90 transition-opacity"
      />
      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setLightbox(false)}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        >
          <button
            type="button"
            onClick={() => setLightbox(false)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
            aria-label="關閉"
          >
            <XIcon className="h-5 w-5" />
          </button>
          <img src={src} alt={alt} className="max-h-[90vh] max-w-[90vw] object-contain" />
        </div>
      )}
    </>
  );
}

export default function ChatMarkdown({ content }: ChatMarkdownProps): React.ReactElement {
  const navigate = useNavigate();
  return (
    <div className="chat-markdown text-sm leading-relaxed break-words">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, SANITIZE_SCHEMA]]}
        components={{
          // ![alt](r2:KEY) → fetch via apiFetch → blob URL → thumbnail
          // + click lightbox. Plain http/https img passes straight through.
          img: ({ src, alt }) => {
            if (typeof src === "string" && src.startsWith(R2_PREFIX)) {
              return <R2Image r2Key={src.slice(R2_PREFIX.length)} alt={alt ?? ""} />;
            }
            return (
              <img src={src} alt={alt ?? ""} loading="lazy" className="my-1 max-h-48 max-w-full rounded border border-border" />
            );
          },
          // External links open in a new tab to avoid blowing away the
          // dashboard session. rel="noopener noreferrer" closes the
          // window.opener tab-napping vector.
          //
          // EXCEPTION: `#entity:<kind>/<id>` hrefs render as inline
          // entity chips with react-router navigation (Plan B). The
          // sanitizer passes these through because fragment-URLs are
          // unrestricted in the default schema.
          a: ({ children, href, ...rest }) => {
            const meta = parseEntityChip(href ?? "");
            if (meta) {
              const Icon = meta.Icon;
              return (
                <button
                  type="button"
                  onClick={() => navigate(meta.route)}
                  className="inline-flex items-center gap-1 mx-0.5 px-1.5 py-0 rounded-md border border-accent/40 bg-accent/10 text-accent hover:bg-accent/20 transition-colors text-xs align-baseline"
                  title={`${meta.kind}:${meta.id}`}
                >
                  <Icon className="h-3 w-3 shrink-0" />
                  <span>{children}</span>
                  <ChevronRight className="h-3 w-3 opacity-60 shrink-0" />
                </button>
              );
            }
            return (
              <a
                {...rest}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline hover:text-accent-hover"
              >
                {children}
              </a>
            );
          },
          // Tables: card-style container with overflow scroll on mobile.
          table: ({ children }) => (
            <div className="my-2 overflow-x-auto rounded border border-border">
              <table className="w-full text-xs">{children}</table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-bg-raised text-text-muted">{children}</thead>
          ),
          th: ({ children }) => (
            <th className="text-left font-medium px-2 py-1.5 border-b border-border">{children}</th>
          ),
          td: ({ children }) => (
            <td className="px-2 py-1.5 border-b border-border-subtle text-text-primary align-top">{children}</td>
          ),
          // Inline code vs code blocks. We don't ship a syntax highlighter
          // (bundle cost not worth it for dashboard chat); plain mono is
          // enough to distinguish code from prose.
          code: ({ className, children, ...rest }) => {
            const isBlock = (className ?? "").startsWith("language-");
            if (isBlock) {
              return (
                <pre className="my-2 overflow-x-auto rounded border border-border bg-bg-raised p-2 text-xs">
                  <code {...rest}>{children}</code>
                </pre>
              );
            }
            return (
              <code className="px-1 py-0.5 rounded bg-bg-raised text-2xs font-mono text-text-primary">
                {children}
              </code>
            );
          },
          // Tight list spacing — default markdown leaves big gaps that
          // wreck conversational density.
          ul: ({ children }) => <ul className="list-disc pl-5 my-1 space-y-0.5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 my-1 space-y-0.5">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          // Headings render smaller than default — dashboard chat bubble
          // is a small surface, h1 would be visually shouting.
          h1: ({ children }) => <h1 className="mt-2 mb-1 text-base font-semibold text-text-primary">{children}</h1>,
          h2: ({ children }) => <h2 className="mt-2 mb-1 text-sm font-semibold text-text-primary">{children}</h2>,
          h3: ({ children }) => <h3 className="mt-2 mb-1 text-sm font-medium text-text-primary">{children}</h3>,
          // Strong / em explicit so we don't depend on `prose` plugin.
          strong: ({ children }) => <strong className="font-semibold text-text-primary">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          blockquote: ({ children }) => (
            <blockquote className="my-2 border-l-2 border-border pl-2 text-text-secondary italic">
              {children}
            </blockquote>
          ),
          // Tighten paragraph spacing inside a small chat bubble.
          p: ({ children }) => <p className="my-1">{children}</p>,
          hr: () => <hr className="my-2 border-border" />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
